import React, { useMemo } from "react";
import { Button, Popconfirm, Tooltip, Popover } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
  PlusOutlined,
  ClusterOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import "./layouts-styles.css";
import { MoreHorizOutlined } from "@mui/icons-material";
import { checkPermissions } from "@comps/commonFunctions";

const classes = "da-px-10 da-my-0";

const getActionButtonClass = (tone) =>
  [classes, "base-table-action-btn", tone ? `base-table-action-btn--${tone}` : ""]
    .filter(Boolean)
    .join(" ");

const BaseAction = (props) => {
  const { modkey } = { ...props };

  // cache permission checks (run once per render)
  const canCancel = useMemo(() => checkPermissions(modkey, "cancel"), [modkey]);
  const canView = useMemo(() => checkPermissions(modkey, "view"), [modkey]);
  const canUpdate = useMemo(() => checkPermissions(modkey, "update"), [modkey]);
  const canDelete = useMemo(() => checkPermissions(modkey, "delete"), [modkey]);
  const isApprovalAction = useMemo(
    () => typeof modkey === "string" && (modkey.toLowerCase().includes("approval") || modkey.toLowerCase().includes("delegates")),
    [modkey]
  );

  const commonActions = [
    props.onView && canView ? (
      <Tooltip title="View" key="view">
        <Button
          className={ getActionButtonClass("view") }
          type="link"
          size="middle"
          onClick={ () => view(props) }
        >
          <EyeOutlined className="icon-style" />
        </Button>
      </Tooltip>
    ) : null,
    props.onEdit && canUpdate ? (
      <Tooltip title={ isApprovalAction ? "Approve/Reject Request" : "Edit" } key="edit">
        <Button
          className={ getActionButtonClass(isApprovalAction ? "approve" : "edit") }
          type="link"
          size="middle"
          onClick={ () => edit(props) }
        >
          { isApprovalAction ? (
            <CheckCircleOutlined className="icon-style" />
          ) : (
            <EditOutlined className="icon-style" />
          ) }
        </Button>
      </Tooltip>
    ) : null,
    props.onDelete && canDelete ? (
      <Popconfirm
        key="delete"
        title="Are you sure?"
        icon={ <WarningOutlined /> }
        onConfirm={ () => del(props) }
      >
        <Tooltip title="Delete">
          <Button className={ getActionButtonClass("delete") } type="link" size="middle">
            <DeleteOutlined className="icon-style" />
          </Button>
        </Tooltip>
      </Popconfirm>
    ) : null,
  ].filter(Boolean);

  const extraActions = [
    props.onCancelRequest && canCancel ? (
      <Tooltip title="Cancel Request" key="cancel">
        <Button
          className={ getActionButtonClass("cancel") }
          type="link"
          size="middle"
          onClick={ () => cancelRequest(props) }
        >
          <StopOutlined className="icon-style" />
        </Button>
      </Tooltip>
    ) : null,
    props.onViewFlow && canView ? (
      <Tooltip title="Flow" key="flow">
        <Button
          className={ getActionButtonClass("flow") }
          type="link"
          size="middle"
          onClick={ () => viewFlow(props) }
        >
          <ClusterOutlined className="icon-style" />
        </Button>
      </Tooltip>
    ) : null,
    props.onViewHistory && canView ? (
      <Tooltip title="History" key="history">
        <Button
          className={ getActionButtonClass("history") }
          type="link"
          size="middle"
          onClick={ () => viewHistory(props) }
        >
          <ClockCircleOutlined className="icon-style" />
        </Button>
      </Tooltip>
    ) : null,
    props.onAdd ? (
      <Tooltip title="Add New" key="add">
        <Button
          className={ getActionButtonClass("add") }
          type="link"
          size="middle"
          onClick={ () => add(props) }
        >
          <PlusOutlined className="icon-style" />
        </Button>
      </Tooltip>
    ) : null,
    props.children ? <React.Fragment key="children">{ props.children }</React.Fragment> : null,
  ].filter(Boolean);

  const dropdown = () => (
    <div className="base-table-actions-overflow">
      { extraActions }
    </div>
  );

  return (
    <div className="base-table-actions-inline">
      { commonActions }
      { extraActions.length > 0 ? (
        <Popover content={ dropdown } title="" trigger="click" placement="top">
          <MoreHorizOutlined
            className="text-primary base-table-actions-trigger"
            style={ {
              cursor: "pointer",
            } }
          />
        </Popover>
      ) : null }
    </div>
  );
};

const cancelRequest = (props) =>
  props.onCancelRequest && props.onCancelRequest(props.each);
const viewFlow = (props) =>
  props.onViewFlow && props.onViewFlow(props.each);
const viewHistory = (props) =>
  props.onViewHistory && props.onViewHistory(props.each);
const view = (props) => props.onView && props.onView(props.each);
const edit = (props) => props.onEdit && props.onEdit(props.each);
const add = (props) => props.onAdd && props.onAdd(props.each);
const del = (props) => props.onDelete && props.onDelete(props.each);

export default BaseAction;
